import React, { useEffect, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, MeshRefractionMaterial } from '@react-three/drei';
import * as THREE from 'three';

useGLTF.preload('/models/diamond_ring.glb');

const desktop = [
  { position: [1.35, -0.64, 0.23], rotation: [-0.1, 0.5, 0.3] },
  { position: [0, -1.54, 0.3], rotation: [0.3, Math.PI, 0] },
  { position: [0, -0.5, 0], rotation: [0, -Math.PI / 2, 0] },
  { position: [0, -0.5, 0], rotation: [0, -Math.PI / 2, 0] }, //phone only
  { position: [-1.3, -0.3, 0], rotation: [-1.3, -Math.PI / 4, -1.9] },
  { position: [-1.5, -0.3, -0.5], rotation: [1.35, -Math.PI / 2, 0] },
];

const mobile = [
  { position: [0, -0.85, 0.3], rotation: [0, 0, 0] },
  { position: [0, -2.0, 0], rotation: [0, Math.PI, 0] },
  { position: [0.7, -0.2, 0], rotation: [0, 1.5 * Math.PI , 0] },
  { position: [-0.7, -0.2, 0], rotation: [0, 1.5 * Math.PI , 0] }, //phone only
  { position: [-1.7, -0.2, 0], rotation: [0, 1.5 * Math.PI , 0] },
  { position: [0, -0.5, -2.5], rotation: [1.35, Math.PI / 2, 0] },
];

const salePage = [
  {position: [0, -0.3, 1], rotation: [0.5, 0, 0]},
  {position: [0, -6, 0], rotation: [0, 0, 0]},
  {position: [0, 0, 0], rotation: [0, 0, 0]},
  {position: [0, 0, 0], rotation: [0, 0, 0]},
  {position: [0, 0, 0], rotation: [0, 0, 0]},
  {position: [0, 0, 0], rotation: [0, 0, 0]}
]

export default function Ring({ currentSection, envMap, isSalePage }) {
  const isMobile =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  const poses = isSalePage ? salePage : (isMobile ? mobile : desktop);

  const group = useRef();
  const { scene } = useGLTF('/models/diamond_ring.glb');
  const [diamonds, setDiamonds] = useState([]);

  /* Assign bespoke materials + extract diamond sub‑meshes */
  useEffect(() => {
    const gems = [];
    scene.traverse(child => {
      if (!child.isMesh) return;
      const name = child.name.toLowerCase();
      if (name.includes('gold')) {
        child.material = new THREE.MeshPhysicalMaterial({
          color: '#f6d365',
          metalness: 0.9,
          roughness: 0.3,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
          envMap,
          envMapIntensity: 1,
        });
      } else if (name.includes('silver')) {
        child.material = new THREE.MeshPhysicalMaterial({
          color: '#c0c0c0',
          metalness: 1,
          roughness: 0.25,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
          envMap,
          envMapIntensity: 1.5,
        });
      } else if (name.includes('diamond')) {
        gems.push({
          geometry: child.geometry,
          position: child.position.clone(),
          rotation: child.rotation.clone(),
          scale: child.scale.clone(),
        });
        child.visible = false; // hide original so we can apply refraction material
      }
    });
    setDiamonds(gems);
  }, [scene, envMap]);

  /* Interpolate ring toward the pose for the current section */
  useFrame((_, delta) => {
    if (!group.current) return;

    const { position: p, rotation: r } = poses[currentSection] ?? poses[0];

    group.current.position.lerp(new THREE.Vector3(...p), 2 * delta);

    group.current.rotation.x = THREE.MathUtils.lerp(
      group.current.rotation.x,
      r[0],
      2 * delta,
    );
    group.current.rotation.z = THREE.MathUtils.lerp(
      group.current.rotation.z,
      r[2],
      2 * delta,
    );
    if (isSalePage) {
      group.current.rotation.y += delta * 0.8;   // ≈ 46 deg / s
      return;   // skip the section‑pose interpolation
    }
    group.current.rotation.y = THREE.MathUtils.lerp(
      group.current.rotation.y,
      r[1],
      2 * delta,
    );
  });

  return (
    <group ref={group}>
      <primitive object={scene} />
      {diamonds.map((d, i) => (
        <mesh
          key={i}
          geometry={d.geometry}
          position={d.position}
          rotation={d.rotation}
          scale={d.scale}
          frustumCulled={false}
        >
          <MeshRefractionMaterial
            envMap={envMap}
            ior={2.4}
            bounces={6}
            aberrationStrength={0.02}
            toneMapped={false}
            // frustumCulled={false}
          />
        </mesh>
      ))}
    </group>
  );
}